import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mic, CheckCircle, FileText, Sparkles, Volume2, Pause, Clock } from "lucide-react";
import { Badge } from "../ui/badge";

const transcriptLines = [
  { speaker: "Dr. Smith", text: "Patient presents with chief complaint of sensitivity lower left, two weeks duration." },
  { speaker: "Dr. Smith", text: "Exam findings — tooth nineteen, recurrent decay distal surface. Percussion negative, vitality positive." },
  { speaker: "Dr. Smith", text: "BOP noted eighteen nineteen. We'll proceed with a two-surface composite, DO, on nineteen." },
  { speaker: "Dr. Smith", text: "Administered two percent lidocaine, one-to-one-hundred-K epi, inferior alveolar block." },
  { speaker: "Dr. Smith", text: "Prep complete. Placing composite, curing. Adjusting occlusion, checking contacts. Looks good." },
  { speaker: "Dr. Smith", text: "Post-op — avoid hard foods twenty-four hours. OTC analgesics as needed. Return for six-month recall." },
];

const generatedNote = {
  sections: [
    {
      title: "Chief Complaint",
      content: "Patient presents with sensitivity to cold on lower left quadrant, onset 2 weeks ago.",
    },
    {
      title: "Clinical Findings",
      content: "Tooth #19: recurrent decay distal surface. Percussion negative, vitality positive. BOP noted on #18-19.",
    },
    {
      title: "Treatment Rendered",
      content: "D2392 — Composite restoration, 2 surfaces (DO) on tooth #19. Local anesthesia: 2% Lidocaine 1:100K epi, IA block.",
    },
    {
      title: "Post-Op Instructions",
      content: "Avoid hard foods 24h. Mild sensitivity expected 1-2 weeks. OTC analgesics PRN. RTC 6mo recall.",
    },
  ],
  codes: ["D2392", "D0220"],
};

const PMSAIScribeSlide = () => {
  const [phase, setPhase] = useState("listening"); // listening, processing, generated
  const [currentLine, setCurrentLine] = useState(-1);
  const [typedChars, setTypedChars] = useState(0);
  const [waveformActive, setWaveformActive] = useState(false);
  const [processingStep, setProcessingStep] = useState(0);
  const [noteSection, setNoteSection] = useState(-1);
  const [noteSectionChars, setNoteSectionChars] = useState(0);

  const processingSteps = [
    "Transcribing audio...",
    "Extracting clinical entities...",
    "Mapping to CDT codes...",
    "Structuring SOAP note...",
    "Verifying completeness...",
  ];

  useEffect(() => {
    let cancelled = false;
    const cycle = async () => {
      while (!cancelled) {
        // Reset
        setPhase("listening");
        setCurrentLine(-1);
        setTypedChars(0);
        setWaveformActive(false);
        setProcessingStep(0);
        setNoteSection(-1);
        setNoteSectionChars(0);
        await new Promise((r) => setTimeout(r, 1200));

        // Listening phase - show transcript lines appearing
        setWaveformActive(true);
        for (let i = 0; i < transcriptLines.length; i++) {
          if (cancelled) return;
          setCurrentLine(i);
          const text = transcriptLines[i].text;
          for (let c = 0; c <= text.length; c += 4) {
            if (cancelled) return;
            setTypedChars(Math.min(c, text.length));
            await new Promise((r) => setTimeout(r, 25));
          }
          setTypedChars(text.length);
          await new Promise((r) => setTimeout(r, 400));
        }
        setWaveformActive(false);

        if (cancelled) return;
        await new Promise((r) => setTimeout(r, 600));

        // Processing phase
        setPhase("processing");
        for (let i = 0; i < processingSteps.length; i++) {
          if (cancelled) return;
          setProcessingStep(i);
          await new Promise((r) => setTimeout(r, 700));
        }

        if (cancelled) return;
        await new Promise((r) => setTimeout(r, 400));

        // Generated phase - type out the note
        setPhase("generated");
        for (let s = 0; s < generatedNote.sections.length; s++) {
          if (cancelled) return;
          setNoteSection(s);
          const content = generatedNote.sections[s].content;
          for (let c = 0; c <= content.length; c += 3) {
            if (cancelled) return;
            setNoteSectionChars(Math.min(c, content.length));
            await new Promise((r) => setTimeout(r, 20));
          }
          setNoteSectionChars(content.length);
          await new Promise((r) => setTimeout(r, 300));
        }

        await new Promise((r) => setTimeout(r, 5000));
      }
    };
    cycle();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="h-full w-full bg-slate-50 flex items-center justify-center p-12 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0">
        <div className="absolute top-[-10%] right-[-5%] w-[600px] h-[600px] bg-blue-100 rounded-full blur-[100px] opacity-60" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[600px] h-[600px] bg-indigo-100 rounded-full blur-[100px] opacity-60" />
      </div>

      <div className="flex flex-row h-full w-full max-w-[1600px] gap-10 relative z-10">
        {/* Left Side */}
        <div className="w-[38%] flex flex-col justify-center space-y-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-100 text-blue-700 text-sm font-bold border border-blue-200 mb-4">
              <Mic className="w-4 h-4" />
              <span>PMS Feature</span>
            </div>

            <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 leading-tight mb-4">
              AI{" "}
              <span className="text-blue-600">Scribe</span>
            </h2>

            <p className="text-lg text-slate-600 leading-relaxed mb-6">
              Speak naturally during procedures — AI Scribe listens, transcribes,
              extracts clinical entities, maps CDT codes, and generates structured
              clinical notes automatically. Zero typing required.
            </p>
          </motion.div>

          <div className="flex flex-wrap gap-2">
            {[
              "Real-Time Transcription",
              "CDT Code Mapping",
              "SOAP Note Generation",
              "Voice-First",
              "Multi-Speaker",
              "HIPAA Compliant",
            ].map((f, i) => (
              <motion.div
                key={f}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3 + i * 0.08, duration: 0.4 }}
                className="px-3 py-1.5 bg-white rounded-full text-sm font-medium text-slate-700 border border-slate-200 shadow-sm"
              >
                {f}
              </motion.div>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-3 mt-4">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm"
            >
              <div className="flex items-center gap-2 mb-1">
                <Clock className="w-5 h-5 text-blue-600" />
                <span className="text-2xl font-bold text-blue-600">3 min</span>
              </div>
              <div className="text-xs text-slate-500 uppercase font-bold tracking-wide">
                Saved Per Patient
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm"
            >
              <div className="flex items-center gap-2 mb-1">
                <FileText className="w-5 h-5 text-blue-600" />
                <span className="text-2xl font-bold text-blue-600">Auto</span>
              </div>
              <div className="text-xs text-slate-500 uppercase font-bold tracking-wide">
                Drafted Notes
              </div>
            </motion.div>
          </div>
        </div>

        {/* Right Side: AI Scribe Interface */}
        <div className="w-[62%] h-full flex items-center justify-center relative">
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="w-full max-w-4xl bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col h-[600px] relative z-10"
          >
            {/* Header */}
            <div className="h-14 border-b border-slate-100 flex items-center justify-between px-6 bg-slate-50/50">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg text-blue-600">
                  <Mic className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-900 text-sm">AI Scribe</h3>
                  <p className="text-xs text-slate-500">Jennifer Martinez &bull; Dr. Smith, DDS</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {phase === "listening" && waveformActive && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex items-center gap-1 px-3 py-1.5 bg-red-50 border border-red-200 rounded-full"
                  >
                    <motion.div
                      animate={{ scale: [1, 1.3, 1] }}
                      transition={{ duration: 1, repeat: Infinity }}
                      className="w-2 h-2 bg-red-500 rounded-full"
                    />
                    <span className="text-xs font-bold text-red-600">Recording</span>
                  </motion.div>
                )}
                {phase === "processing" && (
                  <div className="flex items-center gap-1 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-full">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      className="w-3 h-3 border-2 border-amber-300 border-t-amber-600 rounded-full"
                    />
                    <span className="text-xs font-bold text-amber-600">Processing</span>
                  </div>
                )}
                {phase === "generated" && (
                  <Badge className="bg-blue-600 text-white hover:bg-blue-700 border-0">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Note Ready
                  </Badge>
                )}
              </div>
            </div>

            <div className="flex-1 flex overflow-hidden">
              {/* Left Panel: Transcript */}
              <div className={`${phase === "generated" ? "w-[45%]" : "w-full"} flex flex-col border-r border-slate-100 transition-all duration-500`}>
                {/* Waveform Visualizer */}
                {phase === "listening" && (
                  <div className="px-6 py-3 border-b border-slate-100 bg-slate-50/30">
                    <div className="flex items-center gap-3">
                      <Volume2 className="w-4 h-4 text-blue-600" />
                      <div className="flex items-center gap-[3px] h-6">
                        {Array.from({ length: 24 }).map((_, i) => (
                          <motion.div
                            key={i}
                            className="w-[3px] bg-blue-400 rounded-full"
                            animate={waveformActive ? {
                              height: [4, Math.random() * 20 + 4, 4],
                            } : { height: 4 }}
                            transition={{
                              duration: 0.4 + Math.random() * 0.3,
                              repeat: Infinity,
                              delay: i * 0.05,
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Processing Steps */}
                {phase === "processing" && (
                  <div className="p-6 space-y-3">
                    <div className="flex items-center gap-2 mb-3">
                      <Sparkles className="w-4 h-4 text-blue-600" />
                      <span className="text-sm font-bold text-slate-700">Generating clinical note...</span>
                    </div>
                    {processingSteps.map((step, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, x: -10 }}
                        animate={processingStep >= i ? { opacity: 1, x: 0 } : {}}
                        transition={{ duration: 0.3 }}
                        className={`flex items-center gap-3 py-2 px-3 rounded-lg ${
                          processingStep === i ? "bg-blue-50 border border-blue-200" : ""
                        }`}
                      >
                        {processingStep > i ? (
                          <CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0" />
                        ) : processingStep === i ? (
                          <motion.div
                            animate={{ rotate: 360 }}
                            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                            className="w-4 h-4 border-2 border-blue-300 border-t-blue-600 rounded-full flex-shrink-0"
                          />
                        ) : (
                          <div className="w-4 h-4 rounded-full border-2 border-slate-200 flex-shrink-0" />
                        )}
                        <span className={`text-sm ${processingStep > i ? "text-slate-500" : processingStep === i ? "text-slate-700 font-medium" : "text-slate-400"}`}>
                          {step}
                        </span>
                      </motion.div>
                    ))}
                  </div>
                )}

                {/* Transcript Lines */}
                {(phase === "listening" || phase === "generated") && (
                  <div className="flex-1 p-4 space-y-2 overflow-y-auto">
                    {phase === "generated" && (
                      <p className="text-[10px] text-slate-400 uppercase font-bold tracking-wide mb-2">Transcript</p>
                    )}
                    {transcriptLines.map((line, i) => {
                      const isActive = phase === "listening" && currentLine === i;
                      const isVisible = phase === "generated" || currentLine >= i;
                      if (!isVisible) return null;

                      const displayText = phase === "generated"
                        ? line.text
                        : currentLine > i
                        ? line.text
                        : line.text.slice(0, typedChars);

                      return (
                        <motion.div
                          key={i}
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3 }}
                          className={`rounded-lg p-2.5 ${
                            isActive ? "bg-blue-50 border border-blue-200" : "bg-slate-50 border border-slate-100"
                          }`}
                        >
                          <div className="flex items-center gap-1.5 mb-1">
                            <div className="w-4 h-4 rounded-full bg-blue-600 flex items-center justify-center text-[8px] text-white font-bold">
                              D
                            </div>
                            <span className="text-[10px] font-bold text-slate-500">{line.speaker}</span>
                          </div>
                          <p className="text-xs text-slate-700 leading-relaxed">
                            {displayText}
                            {isActive && (
                              <motion.span
                                animate={{ opacity: [1, 0] }}
                                transition={{ duration: 0.5, repeat: Infinity }}
                                className="inline-block w-0.5 h-3 bg-blue-600 ml-0.5 align-middle"
                              />
                            )}
                          </p>
                        </motion.div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Right Panel: Generated Note (appears when generated) */}
              {phase === "generated" && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5 }}
                  className="w-[55%] flex flex-col"
                >
                  <div className="px-4 py-3 border-b border-slate-100 bg-blue-50/50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-blue-600" />
                        <span className="text-xs font-bold text-blue-700">Generated Clinical Note</span>
                      </div>
                      <div className="flex items-center gap-1">
                        {generatedNote.codes.map((code) => (
                          <span key={code} className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-[10px] font-bold border border-blue-200">
                            {code}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="flex-1 p-4 space-y-3 overflow-y-auto">
                    {generatedNote.sections.map((section, i) => {
                      const isActive = noteSection === i;
                      const isCompleted = noteSection > i;
                      const displayContent =
                        isActive
                          ? section.content.slice(0, noteSectionChars)
                          : isCompleted
                          ? section.content
                          : "";
                      const isVisible = noteSection >= i;
                      if (!isVisible) return null;

                      return (
                        <motion.div
                          key={i}
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3 }}
                          className={`rounded-xl p-3 border-2 transition-all duration-300 ${
                            isActive
                              ? "bg-blue-50 border-blue-300 ring-2 ring-blue-100"
                              : isCompleted
                              ? "bg-blue-50/50 border-blue-200"
                              : "bg-slate-50 border-slate-200"
                          }`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <p className="text-xs font-bold text-slate-700">{section.title}</p>
                            {isCompleted && <CheckCircle className="w-3.5 h-3.5 text-blue-600" />}
                          </div>
                          <p className="text-sm text-slate-600 leading-relaxed min-h-[18px]">
                            {displayContent}
                            {isActive && (
                              <motion.span
                                animate={{ opacity: [1, 0] }}
                                transition={{ duration: 0.5, repeat: Infinity }}
                                className="inline-block w-0.5 h-3.5 bg-blue-600 ml-0.5 align-middle"
                              />
                            )}
                          </p>
                        </motion.div>
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </div>

            {/* Bottom Bar */}
            <div className="h-14 border-t border-slate-100 bg-gradient-to-r from-blue-50 to-indigo-50 flex items-center justify-between px-6">
              <div className="flex items-center gap-2">
                <Mic className="w-4 h-4 text-blue-600" />
                <span className="text-xs font-bold text-blue-700">Voice-first clinical documentation</span>
              </div>
              <div className="flex items-center gap-1 text-xs font-bold text-blue-600">
                <Sparkles className="w-3 h-3" />
                AI Scribe
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default PMSAIScribeSlide;
